// src/components/atomic-crm/companies/CompanyList.tsx
import { useState, useEffect } from "react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/ds/admin/create-button";
import { ExportButton } from "@/components/ds/admin/export-button";
import { List } from "@/components/ds/admin/list";
import { ListPagination } from "@/components/ds/admin/list-pagination";
import { SortButton } from "@/components/ds/admin/sort-button";
import { Skeleton } from "@/components/ds/ui/skeleton";
import { Card } from "@/components/ds/ui/card";
import { Button } from "@/components/ds/ui/button";
import { Filter, X } from "lucide-react";

import { TopToolbar } from "../layout/TopToolbar";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyListFilter } from "./CompanyListFilter";
import { ImageList } from "./GridList";

export const CompanyList = () => {
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      actions={
        <div className="hidden sm:block">
          <CompanyListActions />
        </div>
      }
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const { identity } = useGetIdentity();
  const [isMobile, setIsMobile] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  // Show loading skeleton while identity or data is loading
  if (!identity || isPending) {
    return <CompanyListSkeleton isMobile={isMobile} />;
  }

  if (!data?.length && !hasFilters) return <CompanyEmpty />;

  // Mobile: Show filter as overlay
  if (isMobile) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex items-center justify-between gap-2">
          {/* Filter left */}
          <Button
            onClick={() => setShowFilter(!showFilter)}
            variant="outline"
            size="sm"
          >
            <Filter className="h-4 w-4" />
            {hasFilters && (
              <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full px-1.5 py-0.5">
                {Object.keys(filterValues).length}
              </span>
            )}
          </Button>

          {/* Actions right */}
          <div className="flex items-center gap-2">
            <SortButton fields={["name", "created_at", "nb_contacts"]} />
            <CreateButton variant="default" />
          </div>
        </div>

        {showFilter && (
          /* filter overlay — unchanged */
          <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
            {/* ... */}
          </div>
        )}

        <Card className="py-0">
          <ImageList />
        </Card>
      </div>
    );
  }
  // Desktop: Show filter as sidebar
  return (
    <div className="w-full flex flex-row gap-8">
      <CompanyListFilter />
      <div className="flex flex-col flex-1 gap-4">
        <ImageList />
      </div>
    </div>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <SortButton fields={["name", "created_at", "nb_contacts"]} />
      <CreateButton  variant={"default"} label="Company" />
    </TopToolbar>
  );
};

const CompanyListSkeleton = ({ isMobile }: { isMobile: boolean }) => {
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Toolbar skeleton */}
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
        </div>
        {/* Row list skeleton — single bordered container with dividers */}
        <div className="w-full rounded-lg border bg-card divide-y divide-border">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-start gap-3 px-3 py-3">
              <Skeleton className="h-9 w-9 rounded-full shrink-0" />
              <div className="flex-1 space-y-2 min-w-0">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop — sidebar + grid, matching the loaded layout
  return (
    <div className="w-full flex flex-row gap-8">
      {/* Sidebar filter skeleton */}
      <div className="w-64 space-y-3">
        <Skeleton className="h-9 w-full rounded-md" /> {/* search */}
        <Skeleton className="h-4 w-20 mt-6" /> {/* "Size" heading */}
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
        <Skeleton className="h-4 w-20 mt-6" /> {/* "Sector" heading */}
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={`s-${i}`} className="h-4 w-full" />
        ))}
      </div>

      {/* Grid skeleton — matches the loaded grid exactly */}
      <div className="flex-1">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className="h-[200px] rounded-lg border border-border/60 bg-card shadow-sm p-4 flex flex-col justify-between"
            >
              {/* Avatar + name skeleton */}
              <div className="flex flex-col items-center gap-2">
                <Skeleton className="h-12 w-12 rounded-full" />
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-20" />
              </div>
              {/* Footer skeleton */}
              <div className="flex justify-between">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

